import React, { useRef, useEffect, useState } from 'react';
import { Box, Snackbar, Alert, useMediaQuery, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector, useDispatch } from "react-redux";

import ResponseContainer from '../components/chatContainer/ResponseContainer';
import NotificationContainer from '../components/notification/NotificationPanel';
import MarkdownInputWithImageUpload from "../components/chatContainer/MarkdownInputWithImageUpload";
import { useUserQuery, useChatHistory } from "../hooks/appHooks";
import { addMessage, clearHistory } from "../store/appSlices";
import config from "../config/config";


const Home = () => {
    const theme = useTheme();
    const isMediumOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const textRef = useRef(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [scrollToBottom, setScrollToBottom] = useState(true);
    const { mutate: chatHistoryQuery, isPending: isChatHistoryPending } = useChatHistory();
    const MAX_CHAR_LIMIT = config.MAX_TOKENS * config.APPROX_CHARS_PER_TOKEN;
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");

    const setScrollToBottomTrue = () => {
        setScrollToBottom(true);
    };

    const {
        mutate: userQuery,
        isPending: isUserQueryPending,
        // error: userQueryError,
        // data: userQueryResponse
    } = useUserQuery(setScrollToBottomTrue);


    const chatHistory = useSelector((state) => state.chat.history);

    // useEffect to listen for logout changes in all tabs
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === "auth" && event.newValue === null) { // Detect logout
                // Clear session and Redux chat history on logout
                sessionStorage.clear();
                dispatch(clearHistory());
                // console.log("Session and chat history cleared due to logout in another tab");
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [dispatch]);


    useEffect(() => {
        if (chatHistory && chatHistory.length === 0) {
            chatHistoryQuery();
        }

    }, []);


    const handleSubmit = (text) => {
        if (text.trim()) {
            const currentText = text.trim();
            if (currentText.length > MAX_CHAR_LIMIT) {
                // Show Snackbar for exceeding max chars
                setSnackbarMessage(`Input exceeds maximum limit of ${config.MAX_TOKENS} tokens.`);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return; // Stop further execution
            }

            // const currentData = { data: currentText, isUser: true };
            // Create a FormData object to append files and text
            const formData = new FormData();
            formData.append("request_data", currentText);
            formData.append("isUser", true);

            if (uploadedFiles.length > 0) {
                // Append each file to the FormData
                uploadedFiles.forEach((file, index) => {
                    formData.append(`file_${index + 1}`, file);
                });
            }

            const currentData = { request_data: currentText, isUser: true };
            dispatch(addMessage(currentData));

            setScrollToBottom(true);

            userQuery(formData);

            textRef.current.value = '';
            setUploadedFiles([]);
        }
    };

    const handleKeyDown = (e) => {

        if (e.key === 'Enter' && !e.shiftKey && !isUserQueryPending) {
            e.preventDefault();
            handleSubmit(textRef.current.value);
        }
    };


    if (isChatHistoryPending) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%', // Ensure it takes up the full available width
                    height: '100%', // Ensure it takes up the full available height
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (

        <Box
            sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'center',
                gap: 1,
                alignItems: "stretch",
            }}
        >

            {/* Main Container to ensure alignment with the side panel */}
            <Box
                // item xs={12} sm={8} md={6} lg={6}
                sx={{
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    gap: 1,
                }}
            >
                {/* <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'auto',
                    }}
                >
                </Box> */}
                <ResponseContainer
                    dataItems={chatHistory}
                    // scrollbarRef={scrollbarRef}
                    scrollToBottom={scrollToBottom}
                    setScrollToBottom={setScrollToBottom}
                />

                {/* Input Textbox and Submit Button */}

                <MarkdownInputWithImageUpload
                    isUserQueryPending={isUserQueryPending}
                    handleKeyDown={handleKeyDown}
                    handleSubmit={handleSubmit}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    textRef={textRef}
                />
            </Box>

            {!isMediumOrSmaller && (
                <NotificationContainer />
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Home;
